import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { IncomePaymentDestination } from "../../../../../../Wrap/Constants/Enums/Domain/IncomePaymentDestination";
import { Money } from "../../../../../../Wrap/Core/DataTypes/Money";
import { DrawdownPaymentDestinationAllocation } from "../../../../../../Wrap/Models/Domains/Drawdown/DrawdownPaymentDestinationAllocation";
import { OneOffIncomePaymentModel } from "../../../../../../Wrap/Models/Models/Processes/Drawdown/OneOffIncomePaymentModel";
import { CheckboxGroup } from "../../../../../Shared/CheckboxGroup/CheckboxGroup";
import { IProcessComponentProps, ProcessComponent } from "../../../../../Shared/Process/Process/ProcessComponent";
import { Typography } from "../../../../../Shared/Typography/Typography";

interface IReviewProps extends IProcessComponentProps {
    data: OneOffIncomePaymentModel;
}

interface IReviewState {
    hasChecked: number[];
}

export class IncomePaymentOneOffReview extends ProcessComponent<IReviewProps, IReviewState> {
    constructor(props: any) {
        super(props);
        this.state = { hasChecked: [] };
    }

    public isValid(): boolean {
        let isValid = super.isValid();
        isValid = isValid && this.state.hasChecked.length == 2;
        if (!isValid) {
            this.props.showErrorMsgPopUp(["Please accept all items to continue"], true);
        }
        return isValid;
    }

    private clientDetailRender() {
        const { Payment, DisinvestmentAllocations } = this.props.data.Instruction.DrawdownSinglePaymentInstruction;
        const {
            CashAvailable,
            CashInstrument,
            EstimatedAssetValueAvailable,
            TotalEstimatedValueAvailable,
        } = this.props.data;

        const paymentAmount: Money = Payment.Amount;
        const nonCashAllocations =
            DisinvestmentAllocations?.filter((x) => x.ProductId != CashInstrument.ProductId) ?? [];

        const config = [
            {
                title: "Payment amount",
                data: MoneyDisplayHelper.getMoneyFormat(paymentAmount),
            },
            {
                title: "Payment type",
                data: nonCashAllocations.length == 0 ? "Available Cash" : "Assets And Available Cash",
            },
        ];

        if (Payment.PaymentDestinationAllocations.length == 1) {
            config.push({
                title: "Bank account",
                data: this.getDestinationDescription(Payment.PaymentDestinationAllocations[0]),
            });
        } else {
            Payment.PaymentDestinationAllocations.map((ele, index) => {
                config.push(
                    {
                        title: "Bank account " + (index + 1),
                        data: this.getDestinationDescription(ele),
                    },
                    {
                        title: "Proportion " + (index + 1),
                        data: ele.Allocation + "%",
                    }
                );
            });
        }

        const fromAssets = nonCashAllocations.reduce((total, el) => total + el.Value.Amount, 0);
        const assetRemaining: Money = {
            Amount: EstimatedAssetValueAvailable.Amount - fromAssets,
            Currency: EstimatedAssetValueAvailable.Currency,
        };
        const cashRemaining: Money = {
            Amount: CashAvailable.Amount - paymentAmount.Amount + fromAssets,
            Currency: CashAvailable.Currency,
        };
        const totalRemaining: Money = {
            Amount: TotalEstimatedValueAvailable.Amount - paymentAmount.Amount,
            Currency: TotalEstimatedValueAvailable.Currency,
        };

        config.push(
            {
                title: "Estimated cash remaining",
                data: MoneyDisplayHelper.getMoneyFormat(cashRemaining),
            },
            {
                title: "Estimated asset value remaining",
                data: MoneyDisplayHelper.getMoneyFormat(assetRemaining),
            },
            {
                title: "Estimated total value remaining",
                data: MoneyDisplayHelper.getMoneyFormat(totalRemaining),
            }
        );

        return (
            <div>
                <div className="d-flex flex-space">
                    <h2 className="mb-11">Income payment details</h2>
                </div>

                <hr className="heading-underlined-text"></hr>
                <Typography type="common" config={config}></Typography>
            </div>
        );
    }

    private getDestinationDescription(destination: DrawdownPaymentDestinationAllocation): string {
        if (destination.Destination == IncomePaymentDestination.WrapCash) {
            return "Wrap Cash";
        }
        const bankAccount = this.props.data.AllBankAccounts.find((x) => x.BankAccountId == destination.BankAccountId);
        return (
            bankAccount.AccountNumber +
            " " +
            bankAccount.BankCodes.BankCode +
            "-" +
            bankAccount.BankCodes.BranchCode +
            "-" +
            bankAccount.BankCodes.ExtraCode +
            " " +
            bankAccount.HolderName
        );
    }

    private renderDeclarations() {
        return (
            <>
                <h2 className="mb-22">Declarations</h2>
                <CheckboxGroup
                    type="block"
                    wrapCss="mb-0"
                    options={[
                        {
                            label: "Take pension benefits from crystallised account declaration",
                            disabled: this.props.isReview,
                            value: this.state.hasChecked.includes(1),
                            onChange: (v) => {
                                const checked = this.state.hasChecked;
                                if (v) {
                                    this.setState({ hasChecked: checked.concat(1) });
                                } else {
                                    this.setState({ hasChecked: checked.filter((ele) => ele != 1) });
                                }
                            },
                        },
                    ]}
                />
                <p className="ml-33">
                    I can confirm that I have provided my client with an illustration in relation to this withdrawal, in
                    line with my regulatory responsibilities as financial adviser, as detailed in FCA Sourcebook COBS
                    14.
                </p>
                <p className="ml-33">
                    I understand that the taxable part of any payment made will be subject to income tax deducted by
                    Standard Life Assurance Limited(SLAL) and paid to HM Revenue & Customs. Standard Life Assurance
                    Limited(SLAL) will submit my personal and income details to HM Revenue & Customs.
                </p>
                <p className="ml-33">
                    I declare that any full payment of the value of benefits under this SIPP shall constitute a full and
                    final discharge of entitlement to receive benefit from this SIPP.
                </p>
                <p className="ml-33">
                    I have read an understood the content of Standard Life Assurance Limited(SLAL)'s leaflet
                    'Comprehensive guide to tax and income withdrawals', and the accompanying documents related to
                    taking income from my account.
                </p>
                <CheckboxGroup
                    type="block"
                    wrapCss="mb-0"
                    options={[
                        {
                            label: "Evidence of age",
                            disabled: this.props.isReview,
                            value: this.state.hasChecked.includes(2),
                            onChange: (v) => {
                                const checked = this.state.hasChecked;
                                if (v) {
                                    this.setState({ hasChecked: checked.concat(2) });
                                } else {
                                    this.setState({ hasChecked: checked.filter((ele) => ele != 2) });
                                }
                            },
                        },
                    ]}
                />

                <p className="ml-33">I confirm that I have seen appropriate evidence of age.</p>
                <p className="ml-33">this can be either:</p>
                <p className="ml-33">A valid driving license or passport, or</p>
                <p className="ml-33">A birth certificate</p>
                <CheckboxGroup
                    type="block"
                    wrapCss="mb-0"
                    options={[
                        {
                            label: "Confirm all",
                            disabled: this.props.isReview,
                            value: this.state.hasChecked.length == 2,
                            onChange: (v) => {
                                if (v) {
                                    this.setState({ hasChecked: [1, 2] });
                                } else {
                                    this.setState({ hasChecked: [] });
                                }
                            },
                        },
                    ]}
                />
            </>
        );
    }

    render() {
        return (
            <>
                <h2 className="mb-24">Review</h2>
                {this.clientDetailRender()}
                {this.renderDeclarations()}
            </>
        );
    }
}
